import { type FC, useMemo, useState, useCallback, type KeyboardEvent, type SyntheticEvent } from 'react';

import { Icon, type IconTypes } from '@vakantiesnl/components/src/atoms/Icon';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { UnorderedList } from '@vakantiesnl/components/src/atoms/UnorderedList';
import { AccommodationContactBlock } from '@vakantiesnl/components/src/blocks/AccommodationContactBlock';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { distanceTranslator, valueTranslator } from '@vakantiesnl/services/src/nbc/getInformation';
import { type Search, type Elastic, type MicroCopy } from '@vakantiesnl/types';
import { FACILITY_GROUP_NAME } from '@vakantiesnl/types/src/elasticsearch';

import { useStyles } from './AccommodationFacts.style';

type Props = {
	/** The accommodation for which the facilities belong to. */
	accommodation: Search.FullAccommodationItem;
};

export const AccommodationFacts: FC<Props> = ({ accommodation }) => {
	const microCopies = useMicroCopyContext();
	const { classes } = useStyles();
	const { isDesktop } = useBreakpoints();
	const [shownGroupContent, setShownGroupContent] = useState<FACILITY_GROUP_NAME | undefined>(undefined);

	const facts = useMemo(
		() => accommodation.facilities as Elastic.FormattedFacts[] | undefined,
		[accommodation.facilities],
	);

	/** Showing only specific facility groups in the main block. */
	const filteredFacts = useMemo(() => {
		/** Not showing 'hygiene', 'hotel-type' and 'campsite-information' anywhere. */
		const groupsToShow = [
			FACILITY_GROUP_NAME.FACILITIES,
			FACILITY_GROUP_NAME.ROOM_FACILITIES,
			FACILITY_GROUP_NAME.BEACH,
			FACILITY_GROUP_NAME.SPORT_ENTERTAINMENT,
		];

		if (!!facts?.length) {
			return facts.filter((factGroup) => groupsToShow.includes(factGroup.name));
		}
		return [];
	}, [facts]);

	/** Showing the other facilities in another block. */
	const otherFacts = useMemo(() => {
		const groupsToShow = [
			FACILITY_GROUP_NAME.BUILDING_INFORMATION,
			FACILITY_GROUP_NAME.PAYMENT,
			FACILITY_GROUP_NAME.DISTANCES,
		];

		if (!!facts?.length) {
			return facts.filter((factGroup) => groupsToShow.includes(factGroup.name));
		}
		return [];
	}, [facts]);

	const handleGroupToggle = useCallback(
		(event: SyntheticEvent<HTMLDivElement>) => {
			const shownGroupContentValue = shownGroupContent;
			const toggledGroup = (event.target as HTMLElement).id as FACILITY_GROUP_NAME;

			if (!!shownGroupContentValue && shownGroupContentValue === toggledGroup) {
				/** Closing the group that's now open. */
				setShownGroupContent(undefined);
			} else {
				/** Opening a group. */
				setShownGroupContent(toggledGroup);
			}

			import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
				import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ trackFacilityGroupToggle }) =>
					track(
						trackFacilityGroupToggle(
							accommodation,
							shownGroupContentValue !== toggledGroup,
							microCopies[`fact.group.${toggledGroup}`],
						),
					),
				),
			);
		},
		[accommodation, microCopies, shownGroupContent],
	);

	const handleGroupTitleKeyDown = useCallback(
		(event: KeyboardEvent<HTMLDivElement>) => {
			event.preventDefault();
			if (event.key === 'Enter' || event.key === ' ') {
				handleGroupToggle(event);
			}
		},
		[handleGroupToggle],
	);

	const GroupTitle = useCallback(
		({ groupType }: { groupType: FACILITY_GROUP_NAME }) => {
			const groupName = microCopies[`fact.group.${groupType}`];
			const iconType = getIcon(groupType);

			return (
				<div className={classes.groupTitle}>
					{!!iconType && <Icon type={iconType} />}
					<Typography variant="headingXs" as="h5" className={classes.groupTitle}>
						{groupName}
					</Typography>
				</div>
			);
		},
		[classes.groupTitle, microCopies],
	);

	const FactList = useCallback(
		({ isMainList }: { isMainList: boolean }) => {
			return (
				<div className={classes.factGroupContainer}>
					{(isMainList ? filteredFacts : otherFacts).map((factGroup: Elastic.FormattedFacts) => {
						const groupName = microCopies[`fact.group.${factGroup.name}`];

						/** Hide the group if we don't have a microCopy available */
						if (!groupName) {
							return null;
						}

						const namedFacts: string[] = factGroup.groups
							.filter((facility) => !!getValue(facility, microCopies))
							.map((facility) => {
								const value = getValue(facility, microCopies);
								const facilityName = microCopies[`fact.name.${facility.name}`] || facility.name;

								if (typeof value === 'boolean') {
									return facilityName;
								}
								return `${facilityName}: ${value}`;
							});

						if (!namedFacts.length) {
							return null;
						}

						const isGroupOpen = shownGroupContent === factGroup.name;

						return (
							<div key={factGroup.key} className={classes.factGroup}>
								{!isDesktop ? (
									<div
										onClick={handleGroupToggle}
										onKeyDown={handleGroupTitleKeyDown}
										className={classes.factHeading}
										role="button"
										tabIndex={0}
										id={factGroup.name}
									>
										<GroupTitle groupType={factGroup.name} />
										<Icon type={isGroupOpen ? 'chevronUp' : 'chevronDown'} />
									</div>
								) : (
									<GroupTitle groupType={factGroup.name} />
								)}

								{(isGroupOpen || isDesktop) && (
									<UnorderedList items={namedFacts} customClassName={classes.factList} />
								)}
							</div>
						);
					})}
				</div>
			);
		},
		[
			GroupTitle,
			classes.factGroup,
			classes.factGroupContainer,
			classes.factHeading,
			classes.factList,
			filteredFacts,
			handleGroupTitleKeyDown,
			handleGroupToggle,
			isDesktop,
			microCopies,
			otherFacts,
			shownGroupContent,
		],
	);

	return (
		<div className={classes.factsContainer}>
			{!!filteredFacts.length && (
				<div>
					<Typography variant={isDesktop ? 'headingXl' : 'headingMd'} as="h4">
						{microCopies['fact.group.facilities']}
					</Typography>
					<FactList isMainList={true} />
				</div>
			)}
			{!!otherFacts.length && (
				<div>
					<Typography variant={isDesktop ? 'headingXl' : 'headingMd'} as="h4">
						{microCopies['fact.otherInfos']}
					</Typography>
					<FactList isMainList={false} />
				</div>
			)}
			{(accommodation.isBookable || !accommodation.premiumAdlinkEnabled) && (
				<div>
					<Typography variant={isDesktop ? 'headingXl' : 'headingMd'} as="h4">
						{microCopies[`accommodation.contactInformation`]}
					</Typography>
					<AccommodationContactBlock
						accommodation={accommodation}
						isPremium={accommodation.isBookable}
						showTitle={false}
					/>
				</div>
			)}
		</div>
	);
};

const getIcon = (groupName: FACILITY_GROUP_NAME): IconTypes | undefined => {
	switch (groupName) {
		case FACILITY_GROUP_NAME.FACILITIES:
			return 'accommodation';

		case FACILITY_GROUP_NAME.ROOM_FACILITIES:
			return 'room';

		case FACILITY_GROUP_NAME.SPORT_ENTERTAINMENT:
			return 'entertainment';

		case FACILITY_GROUP_NAME.BEACH:
			return 'pool';

		default:
			return undefined;
	}
};

const getValue = (facility: Elastic.FetchedFact, microCopies: MicroCopy): string | boolean | undefined => {
	switch (facility.group) {
		case 'distances':
			return distanceTranslator(facility.value, microCopies, false);

		case 'building-information':
			return facility.value.toString();

		default:
			const { isAvailable } = valueTranslator(facility.value, microCopies);
			return isAvailable;
	}
};
